import React, { useState, useEffect } from 'react';
import { Alert, Button, Toast, ToastContainer } from 'react-bootstrap';
import { Capacitor } from '@capacitor/core';

/** PWA install prompts are for browser users; hide in Play Store / Capacitor shell and when already installed. */
function shouldOfferPwaInstall() {
  try {
    if (Capacitor.isNativePlatform()) return false;
  } catch (_) {
    /* ignore */
  }
  if (window.matchMedia('(display-mode: standalone)').matches) return false;
  return true;
}

const PWAManager = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPersistentInstallButton, setShowPersistentInstallButton] = useState(
    () => shouldOfferPwaInstall()
  );
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [showOfflineToast, setShowOfflineToast] = useState(false);

  useEffect(() => {
    const offerPwaInstall = shouldOfferPwaInstall();

    // Handle online/offline status
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineToast(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineToast(true);
    };

    // Handle PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    // Handle app installed
    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      setShowPersistentInstallButton(false);
      console.log('StreamingMood PWA installed');
    };

    // Handle service worker updates
    const handleSWUpdate = () => {
      setShowUpdateToast(true);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (offerPwaInstall) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);
    }

    // Check for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleSWUpdate);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (offerPwaInstall) {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      }
      
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleSWUpdate);
      }
    };
  }, []);

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      // Automatic PWA install prompt available
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log('PWA install outcome:', outcome);
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      if (outcome === 'accepted') {
        setShowPersistentInstallButton(false);
      }
    } else {
      // Show manual installation instructions
      alert(`📱 Install StreamingMood App:

        DESKTOP:
• Chrome: Click "Open in App" in address bar
• Or: Chrome menu → "Install StreamingMood..."

MOBILE CHROME:
• Method 1: Menu (⋮) → "Add to Home screen"
• Method 2: Try hostname: DCIL-LHFM40MQ0T.local:3000
• Method 3: Use app for 2+ minutes, then check menu
• Method 4: Desktop site mode + long press address bar

MOBILE SAFARI:
• Share button → "Add to Home Screen"

Note: PWA install may require HTTPS on mobile devices.
For full PWA experience, use production version at streamingmood.com`);
    }

    // Track installation
    if (window.gtag) {
      window.gtag('event', 'pwa_install_prompt', {
        event_category: 'PWA',
        event_label: 'install_prompt_shown'
      });
    }
  };

  const handleUpdateApp = () => {
    window.location.reload();
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        
        // Register for push notifications if supported
        if ('serviceWorker' in navigator && 'PushManager' in window) {
          try {
            await navigator.serviceWorker.ready;
            // You would implement push subscription here
            console.log('Push messaging is supported');
          } catch (error) {
            console.error('Push subscription failed:', error);
          }
        }
      }
    }
  };

  return (
    <>
      {/* Install App Prompt */}
      {showInstallPrompt && (
        <Alert 
          variant="info" 
          dismissible 
          onClose={() => setShowInstallPrompt(false)}
          className="position-fixed"
          style={{ 
            bottom: '20px', 
            left: '20px', 
            right: '20px', 
            zIndex: 1050,
            maxWidth: '400px',
            margin: '0 auto'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <strong><span role="img" aria-label="mobile phone">📱</span> Install StreamingMood</strong>
              <div className="small text-muted">
                Get the app experience with offline access!
              </div>
            </div>
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleInstallApp}
              className="ms-2"
            >
              Install
            </Button>
          </div>
        </Alert>
      )}

      {/* Persistent Install Button - Always Visible */}
      {showPersistentInstallButton && (
        <Button
          variant="outline-light"
          size="sm"
          onClick={handleInstallApp}
          className="position-fixed"
          style={{
            bottom: '20px',
            right: '20px',
            zIndex: 999,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '600',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.2s ease',
            pointerEvents: 'auto',
            width: 'auto',
            height: 'auto'
          }}
        >
          <span role="img" aria-label="mobile phone">📱</span> Install App
        </Button>
      )}

      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        {/* App Update Available */}
        <Toast 
          show={showUpdateToast} 
          onClose={() => setShowUpdateToast(false)}
          delay={10000}
          autohide
        >
          <Toast.Header>
            <span role="img" aria-label="update" className="me-2">🔄</span>
            <strong className="me-auto">Update Available</strong>
          </Toast.Header>
          <Toast.Body>
            <div className="d-flex justify-content-between align-items-center">
              <span>New version of StreamingMood is ready!</span>
              <Button variant="primary" size="sm" onClick={handleUpdateApp}>
                Update
              </Button>
            </div>
          </Toast.Body>
        </Toast>

        {/* Offline Status */}
        <Toast 
          show={showOfflineToast} 
          onClose={() => setShowOfflineToast(false)}
          bg="warning"
        >
          <Toast.Header>
            <span role="img" aria-label="offline" className="me-2">📵</span>
            <strong className="me-auto">You're Offline</strong>
          </Toast.Header>
          <Toast.Body>
            <div className="d-flex justify-content-between align-items-center">
              <span>Some features may be limited.</span>
              <Button 
                variant="outline-light" 
                size="sm" 
                onClick={requestNotificationPermission}
              >
                Enable Notifications
              </Button>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Network Status Indicator */}
      {!isOnline && (
        <div 
          className="position-fixed top-0 start-0 end-0 bg-warning text-dark text-center py-1"
          style={{ zIndex: 1060, fontSize: '0.875rem' }}
        >
          <span role="img" aria-label="offline">📵</span> 
          {' '}Offline Mode - Limited functionality available
        </div>
      )}
    </>
  );
};

export default PWAManager;
